import React, {useEffect} from "react";
import styles from "./FranchiseTable.module.css";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {
    fetchFranchises,
    franchiseListSelector,
    franchiseListStatusSelector,
    franchiseTableRequestSelector,
    franchiseTotalCountSelector,
    FRANCHISE_TABLE_ITEMS_ON_PAGE,
    updateFranchiseTableRequest
} from "../franchiseSlice";
import {RequestStatus} from "../../../../api/apiConfiguration";
import {TableLoader} from "../../../elements/tableLoader/TableLoader";
import {TablePagination} from "../../../elements/pagination/TablePagination";
import {Button, ButtonState} from "../../../elements/button/Button";
import {FormName, openForm} from "../../../elements/formProvider/formSlice";
import {FranchiseTableControls} from "./controls/FranchiseTableControls";
import {FranchiseTableHeader} from "./header/FranchiseTableHeader";
import {FranchiseTableRow} from "./row/FranchiseTableRow";

export const FranchiseTable: React.FC = () => {
    const dispatch = useAppDispatch();
    const franchises = useAppSelector(franchiseListSelector);
    const status = useAppSelector(franchiseListStatusSelector);
    const tableRequest = useAppSelector(franchiseTableRequestSelector);
    const totalCount = useAppSelector(franchiseTotalCountSelector);

    useEffect(() => {
        dispatch(fetchFranchises());
    }, [dispatch, tableRequest]);

    useEffect(() => {
        if (franchises.length === 0 && tableRequest.pageNumber > 0 && status === RequestStatus.RESOLVED) {
            dispatch(updateFranchiseTableRequest({pageNumber: tableRequest.pageNumber - 1}));
        }
    }, [dispatch, franchises.length, status, tableRequest.pageNumber]);

    const openCreate = () => dispatch(openForm({name: FormName.CREATE_FRANCHISE}));

    const hasActiveFilters = () => {
        return !!tableRequest.searchQuery || tableRequest.locationPublicIds.length > 0;
    };

    const isLoading = status === RequestStatus.PENDING;
    const isResolved = status === RequestStatus.RESOLVED;
    const showEmptyState = isResolved && totalCount === 0 && !hasActiveFilters();

    return <div className={styles.wrapper}>
        <div className={styles.tableWrapper}>
            <FranchiseTableControls/>
            <FranchiseTableHeader/>
            <div className={styles.tableRowsWrapper}>
                {isLoading && <TableLoader/>}
                {isResolved && franchises.map((franchise, index) => (
                    <FranchiseTableRow
                        key={franchise.publicId}
                        index={index}
                        franchise={franchise}
                        isLastRow={index === franchises.length - 1}
                    />
                ))}
            </div>
        </div>

        {showEmptyState ? (
            <div className={styles.emptyTableWrapper}>
                <div className={styles.titleWrapper}>
                    <span className={styles.title}>You haven’t created any franchise yet</span>
                    <span className={styles.subtitle}>
                        Get started by creating a franchise. Once created, it will appear here.
                    </span>
                </div>
                <Button
                    title={"Create franchise"}
                    iconSrc={"/plusIcon.svg"}
                    state={ButtonState.PRIMARY}
                    handleClick={openCreate}
                />
            </div>
        ) : (
            <TablePagination
                pageNumber={tableRequest.pageNumber}
                totalItems={totalCount}
                itemsPerPage={FRANCHISE_TABLE_ITEMS_ON_PAGE}
                onPageChange={pageNumber => dispatch(updateFranchiseTableRequest({pageNumber}))}
            />
        )}
    </div>;
};
